import { useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ISLAND_ARRIVAL_STAGGER_MS } from '../../../../lib/chat-status-arrival';
import { Check, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useChatStatus } from '../../../../lib/chat-status';
import { bucketContaining } from '../../../../lib/agent-task-buckets';
import { connectHrefFor } from '../../../../lib/suggested-task-connect';
import { connectorDisplayName } from '../../../../lib/connectors';
import { handleModalEscape, pushModalEscape } from '../../../../lib/modal-escape';
import { isGatedTask, isNotStartedTask, isScheduledTask } from '../../../../lib/task-schedule';
import type { WebGoal, WebGoalTask } from '../../../../lib/goals';
import type { WebRoutine } from '../../../../lib/routines';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useMyAgents } from '../../../../lib/my-agents';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { AgentDot } from '../../../AgentDot';
import { LiveGoalDetailsModal } from '../../goals/GoalDetailsModal';
import { RoutineDetailModal } from '../../routines/RoutineDetailModal';
import { useRoutineCopy } from '../../routines/use-routine-copy';
import { useTaskSidePanel } from '../../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../../tasks/TaskDetailModal';
import { Badge, Button, Card, ScrollArea, TextButton, Typography } from '../../ui';
import { ChatFilesList, createdFileAttachments } from '../ChatFilesPanel';
import { fileTypeKind } from '../../../../lib/artifact-image';
import { useBasePath } from '../../../../lib/use-base-path';
import { Grow } from './Grow';
import styles from './ChatStatusIsland.module.css';

const BI_PAGE = { page_name: 'chat_status' };
/** The gap between a section's rows (`.section`, `.goal`). */
const ROW_GAP = 6;

interface IslandArrival {
  className?: string;
  style?: CSSProperties;
  delayMs?: number;
  grows?: boolean;
}

type TaskState = 'idle' | 'working' | 'waiting' | 'done';

/**
 * Shared with both phones: only a completed task is done; cancelled, retired and failed ones sit idle, and so does
 * one not started yet, dated for later or waiting on another task.
 */
function taskState(task: Parameters<typeof isGatedTask>[0]): TaskState {
  const bucket = bucketContaining(task.status);
  if (bucket === 'waiting') return 'waiting';
  if (task.status === 'COMPLETED') return 'done';
  if (bucket === 'inProgress' && !isNotStartedTask(task) && !isScheduledTask(task) && !isGatedTask(task)) return 'working';
  return 'idle';
}

/**
 * The chat status from the agents island on a phone-width chat (Sarah Hindman's island design, 2026-10-20): a
 * panel dropped from under the chat header over a dim scrim, the goal first, then the chat's other tasks, the
 * team, the apps, the routines, the files it wrote and the knowledge it saved, with the one task waiting on the
 * reader on top. The same model as the desktop column
 * (`lib/chat-status.ts`); iOS and Android draw the same panel behind `symphonyChatStatusIslandNative`.
 */
export function ChatStatusIsland({
  conversationId,
  anchor,
  onClose,
  peek = false,
  fresh,
  onKeepOpen,
}: {
  conversationId: string | null;
  /** The chat header: the panel starts right under it. */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Dropped open by itself because the chat just made something: no dim over the chat, and it closes on its
   *  own. A tap on the panel keeps it open (`onKeepOpen`). */
  peek?: boolean;
  /** The island keys that just arrived (`islandArrivalKeys`), which slide in one after another. */
  fresh?: string[];
  onKeepOpen?: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const leaveChat = useLeaveChatOnNavigate();
  const { openModal } = useTaskSidePanel();
  const maestro = useAgentName();
  const { scheduleText } = useRoutineCopy();
  const state = useChatStatus(conversationId);
  const basePath = useBasePath();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const [top, setTop] = useState<number | null>(null);
  const [routine, setRoutine] = useState<WebRoutine | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return undefined;
    const place = () => setTop(anchor.getBoundingClientRect().bottom);
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  useEffect(() => {
    const release = pushModalEscape(onClose);
    window.addEventListener('keydown', handleModalEscape, true);
    return () => {
      release();
      window.removeEventListener('keydown', handleModalEscape, true);
    };
  }, [onClose]);

  if (state.status !== 'ready' || top === null) return null;
  const { goals, agents, connectors, routines, tasks: chatTasks, files, knowledge } = state.data;
  // Each new section and row comes in at its own turn, in the order the panel draws them. One that arrives
  // under a section the reader already sees grows into place; one that came with its new section only fades.
  const arrive = (key: string, parent?: string): IslandArrival => {
    const index = fresh?.indexOf(key) ?? -1;
    if (index < 0) return {};
    const delayMs = index * ISLAND_ARRIVAL_STAGGER_MS;
    return { className: styles.arrive, style: { animationDelay: `${delayMs}ms` }, delayMs, grows: !(parent && fresh?.includes(parent)) };
  };
  const rowProps = (arrival: IslandArrival, base: string) => ({
    className: arrival.className ? `${base} ${arrival.className}` : base,
    style: arrival.style,
  });
  const growRow = (key: string, arrival: IslandArrival, children: ReactNode) => (
    <Grow key={key} active={Boolean(arrival.grows)} delayMs={arrival.delayMs ?? 0} gap={ROW_GAP}>
      {children}
    </Grow>
  );
  const createdFiles = createdFileAttachments([], files);
  const tasks = goals.flatMap((goal) => goal.tasks);
  // The whole team, not only this chat's hires: a goal's task can sit on an agent hired elsewhere.
  const agentName = (id?: string) => (id ? team.find((agent) => agent.id === id)?.name : undefined) ?? maestro.short;
  const review = [...tasks, ...chatTasks].find((task) => taskState(task) === 'waiting');

  const report = (button: string, extra?: Record<string, string>) =>
    logPageClickBi({ ...BI_PAGE, button_name: button, ...(conversationId ? { conversation_id: conversationId } : {}) }, extra ? { additionalFields: extra } : undefined);
  const openTask = (task: { id: string }, button = 'open_review') => {
    report(button, { task_id: task.id });
    onClose();
    openModal(<TaskDetailModal taskId={task.id} />, task.id);
  };
  const openGoal = (goal: WebGoal) => {
    report('open_goal', { goal_id: goal.id });
    onClose();
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
  };
  const navigate = (button: string, id: string) => {
    report(button, { id });
    onClose();
    leaveChat();
  };

  const done = tasks.filter((task) => taskState(task) === 'done').length;
  const chatTasksDone = chatTasks.filter((task) => taskState(task) === 'done').length;
  const waiting = connectors.filter((connector) => !connector.connected).length;
  const routinesOn = routines.filter((item) => !item.paused).length;
  const agentLine = (id: string) => {
    const own = tasks.filter((task) => task.agentId === id).map(taskState);
    if (own.includes('waiting')) return <Typography variant="p3" className={styles.waitingInk}>{t('chat.status.waitingYourReview')}</Typography>;
    if (own.includes('working')) return <Typography variant="p3" className={styles.workingInk}>{t('chat.status.workingOnIt')}</Typography>;
    return null;
  };

  return (
    <div className={peek ? `${styles.scrim} ${styles.peek}` : styles.scrim} style={{ paddingTop: top }} onClick={peek ? undefined : onClose}>
      <Card
        disablePadding
        className={styles.panel}
        onClick={(e) => {
          e.stopPropagation();
          if (peek) onKeepOpen?.();
        }}
        aria-label={t('chat.status.title')}
      >
      <ScrollArea className={styles.scroll}>
        {review && (
          <div className={styles.review}>
            <span className={styles.reviewMark} aria-hidden>
              !
            </span>
            <div className={styles.grow}>
              <Typography variant="h4">{t('chat.status.needsReview', { name: agentName(review.agentId) })}</Typography>
              <Typography variant="p3" color="secondary" className={styles.ellipsis}>
                {review.title}
              </Typography>
            </div>
            <Button size="small" onClick={() => openTask(review)}>
              {t('chat.status.review')}
            </Button>
          </div>
        )}

        {goals.length > 0 && (
          <Section title={t('chat.status.goal')} summary={t('chat.status.goalProgress', { done, total: tasks.length })} arrival={arrive('card:goal')}>
            {goals.map((goal) => {
              const goalArrival = arrive(`goal:${goal.id}`, 'card:goal');
              return growRow(
                goal.id,
                goalArrival,
                <div {...rowProps(goalArrival, styles.goal)}>
                  <TextButton size="small" className={styles.start} endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />} onClick={() => openGoal(goal)}>
                    {goal.title}
                  </TextButton>
                  {goal.tasks.map((task) => {
                    const stateOf = taskState(task);
                    const taskArrival = arrive(`task:${task.id}`, `goal:${goal.id}`);
                    return growRow(
                      task.id,
                      taskArrival,
                      <div {...rowProps(taskArrival, styles.row)}>
                        <span className={`${styles.circle} ${styles[stateOf]}`} aria-hidden />
                        <div className={styles.grow}>
                          <Typography variant="p3">{task.title}</Typography>
                          {stateOf === 'working' && <Typography variant="p3" className={styles.workingInk}>{t('chat.status.workingOnIt')}</Typography>}
                          {stateOf === 'waiting' && <Typography variant="p3" className={styles.waitingInk}>{t('chat.status.waitingYourReview')}</Typography>}
                        </div>
                        <Typography variant="p3" color="secondary" className={styles.noShrink}>
                          {agentName(task.agentId)}
                        </Typography>
                      </div>,
                    );
                  })}
                </div>,
              );
            })}
          </Section>
        )}

        {chatTasks.length > 0 && (
          <Section
            title={t('chat.status.tasks')}
            summary={t('chat.status.goalProgress', { done: chatTasksDone, total: chatTasks.length })}
            arrival={arrive('card:tasks')}
          >
            {chatTasks.map((task) => {
              const stateOf = taskState(task);
              const taskArrival = arrive(`task:${task.id}`, 'card:tasks');
              return growRow(
                task.id,
                taskArrival,
                <div {...rowProps(taskArrival, styles.row)}>
                  <span className={`${styles.circle} ${styles[stateOf]}`} aria-hidden />
                  <div className={styles.grow}>
                    <TextButton size="small" className={styles.start} onClick={() => openTask(task, 'open_task')}>
                      {task.title}
                    </TextButton>
                    {stateOf === 'working' && <Typography variant="p3" className={styles.workingInk}>{t('chat.status.workingOnIt')}</Typography>}
                    {stateOf === 'waiting' && <Typography variant="p3" className={styles.waitingInk}>{t('chat.status.waitingYourReview')}</Typography>}
                  </div>
                  <Typography variant="p3" color="secondary" className={styles.noShrink}>
                    {agentName(task.agentId)}
                  </Typography>
                </div>,
              );
            })}
          </Section>
        )}

        {agents.length > 0 && (
          <Section
            title={t('chat.status.team')}
            summary={t(agents.length === 1 ? 'chat.status.agents.one' : 'chat.status.agents.other', { count: agents.length })}
            arrival={arrive('card:team')}
          >
            {agents.map((agent) => {
              const agentArrival = arrive(`agent:${agent.id}`, 'card:team');
              return growRow(
                agent.id,
                agentArrival,
              <div {...rowProps(agentArrival, styles.row)}>
                <AgentDot id={agent.id} size={26} />
                <div className={styles.grow}>
                  <TextButton size="small" href={`/agents/${encodeURIComponent(agent.id)}`} className={styles.start} onClick={() => navigate('open_agent', agent.id)}>
                    {agent.name}
                  </TextButton>
                  {agentLine(agent.id)}
                </div>
                {agent.title && (
                  <Typography variant="p3" color="secondary" className={styles.noShrink}>
                    {agent.title}
                  </Typography>
                )}
              </div>,
              );
            })}
          </Section>
        )}

        {connectors.length > 0 && (
          <Section
            title={t('chat.status.connectors')}
            summary={waiting > 0 ? t('chat.status.waiting', { count: waiting }) : t('chat.status.allConnected')}
            arrival={arrive('card:connectors')}
          >
            <div className={styles.chips}>
              {connectors.map((connector) => {
                const name = connectorDisplayName(connector);
                const href = connector.connected ? null : connectHrefFor(connector.toolkit);
                return connector.connected || !href ? (
                  <Badge key={connector.toolkit} variant={connector.connected ? 'connected' : undefined} size="small" label={name} endIcon={connector.connected ? <Check size={14} aria-hidden /> : undefined} />
                ) : (
                  <Button key={connector.toolkit} variant="outlined" size="tiny" href={href} onClick={() => navigate('connect_connector', connector.toolkit)}>
                    {name}
                  </Button>
                );
              })}
            </div>
          </Section>
        )}

        {routines.length > 0 && (
          <Section title={t('chat.status.routines')} summary={t('chat.status.routinesOn', { count: routinesOn })} arrival={arrive('card:routines')}>
            {routines.map((item) => {
              const schedule = scheduleText(item);
              return (
                <TextButton
                  key={item.id}
                  size="small"
                  className={styles.start}
                  onClick={() => {
                    report('open_routine', { routine_id: item.id });
                    setRoutine(item);
                  }}
                >
                  {schedule ? `${item.name ?? ''} · ${schedule}` : item.name ?? ''}
                </TextButton>
              );
            })}
          </Section>
        )}

        {createdFiles.length > 0 && (
          <Section title={t('chat.status.files')} summary={String(createdFiles.length)} arrival={arrive('card:files')}>
            <ChatFilesList conversationId={null} extra={createdFiles} onOpened={onClose} />
          </Section>
        )}

        {knowledge.length > 0 && (
          <Section title={t('chat.status.knowledge')} summary={String(knowledge.length)} arrival={arrive('card:knowledge')}>
            {/* A saved note is a file in the knowledge store, so it reads like one: the explorer's document glyph. */}
            {knowledge.map((entry) => {
              const noteArrival = arrive(`knowledge:${entry.path}`, 'card:knowledge');
              return growRow(
                entry.path,
                noteArrival,
                <div {...rowProps(noteArrival, styles.row)}>
                  <img src={`${basePath}/file-types/${fileTypeKind('md')}.png`} alt="" width={16} height={20} className={styles.fileGlyph} />
                  <TextButton size="small" href="/knowledge" className={styles.start} onClick={() => navigate('open_knowledge', entry.path)}>
                    {entry.titleKey ? t(entry.titleKey) : entry.title}
                  </TextButton>
                </div>,
              );
            })}
          </Section>
        )}

        <Button
          variant="text"
          fullWidth
          href="/agents"
          className={styles.footer}
          endIcon={<ChevronRight size={16} aria-hidden className="dir-flip" />}
          onClick={() => navigate('manage_team', 'agents')}
        >
          {t('chat.status.manageTeam')}
        </Button>
      </ScrollArea>
      </Card>
      {routine && (
        <span onClick={(e) => e.stopPropagation()}>
          <RoutineDetailModal key={routine.id} routine={routine} onClose={() => setRoutine(null)} />
        </span>
      )}
    </div>
  );
}

function Section({ title, summary, arrival, children }: { title: string; summary: string; arrival?: IslandArrival; children: ReactNode }) {
  // The slot carries the separator, so it stays put while the section inside it grows.
  return (
    <div className={styles.slot}>
      <Grow active={Boolean(arrival?.grows)} delayMs={arrival?.delayMs ?? 0}>
        <section className={arrival?.className ? `${styles.section} ${arrival.className}` : styles.section} style={arrival?.style}>
          <div className={styles.sectionHead}>
            <Typography variant="h4">{title}</Typography>
            <Typography variant="p3" color="secondary">
              {summary}
            </Typography>
          </div>
          {children}
        </section>
      </Grow>
    </div>
  );
}
